/* eslint-disable no-console */
import { Api } from 'api';
import React, { useState, useCallback, useEffect } from 'react';
import { useQuery } from 'react-query';
import { ExpertisePackageGroup, ExpertiseQueryCacheKey } from 'types';
import { Box, Text } from '@chakra-ui/react';
import { Select, MultiValue } from 'chakra-react-select';
import { t } from '@lingui/macro';

export interface AdditionalPackageSelectProps {
  onPackageGroupChange?: (selectedGroups: ExpertisePackageGroup[]) => void;
  expertisePackageId: number;
  onChange?: (selectedIds: number[]) => void;
  formikValue?: number[];
  expertisePackagePrice?: string;
}

interface OptionType {
  label: string;
  value: number;
}

export const AdditionalPackageSelect: React.FC<AdditionalPackageSelectProps> = ({
  onPackageGroupChange,
  expertisePackageId,
  onChange,
  formikValue,
  expertisePackagePrice,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<OptionType[]>([]);

  const query = useQuery(
    [ExpertiseQueryCacheKey.GetAllAdditionalPackages, expertisePackageId],
    async () => {
      const response = await Api.Expertise.getAllAdditionalPackages(expertisePackageId);
      return response.data.filter((group: any) => group.canBePurchasedAdditionalService && group.price > 0);
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 0,
      retry: false,
    },
  );

  const handleChange = useCallback(
    (selectedItems: MultiValue<OptionType>) => {
      const newSelectedOptions = selectedItems as OptionType[];
      const newSelectedIds = newSelectedOptions.map((item) => item.value);

      if (onChange) {
        onChange(newSelectedIds);
      }

      if (query.data) {
        const selectedGroups = query.data.filter((group: any) =>
          newSelectedIds.includes(group.abilityDefinitionTypeId),
        );

        if (onPackageGroupChange) {
          onPackageGroupChange(selectedGroups);
        }

        setSelectedOptions(newSelectedOptions);
      }
    },
    [onChange, onPackageGroupChange, query.data],
  );

  const options = React.useMemo(
    () =>
      query.data?.map((group: any) => ({
        label: `${group.name} ${group.price ? `(${group.price} TL)` : ''}`,
        value: group.abilityDefinitionTypeId,
        price: group.price,
      })) || [],
    [query.data],
  );

  useEffect(() => {
    if (formikValue?.length === 0) {
      setSelectedOptions([]);
    }
  }, [formikValue]);

  return (
    <Box>
      <Select
        isMulti
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        closeMenuOnSelect={false}
        placeholder={t`Ek Paketleri seçin`}
        menuPlacement="auto"
        isClearable
        isDisabled={!query.data}
        noOptionsMessage={() => t`Ek Paketleri seçin`}
      />
      {selectedOptions.length > 0 && (
        <Text sx={{ fontSize: '14px', color: '#9ae6b4', mt: 1 }}>
          {t`Toplam Fiyat`}:{' '}
          {selectedOptions.reduce((acc, option: any) => acc + Number(option.price || 0), 0) +
            Number(expertisePackagePrice || 0)}{' '}
          {t`TL`}
        </Text>
      )}
    </Box>
  );
};
